import { formatCurrencyCompact, formatPct2 } from '@/shared/utils/number';

const SEGMENTS = [
  { key: 'A', label: 'A (Salaries)', color: '#059669' },
  { key: 'B', label: 'B (Related)', color: '#0284c7' },
  { key: 'C', label: 'C (Operations)', color: '#64748b' },
  { key: 'D', label: 'D (Exceptional)', color: '#7c3aed' },
] as const;

export interface StackedAmountsABCDProps {
  aMain: number;
  bMain: number;
  cMain: number;
  dMain: number;
  className?: string;
}

function safeNum(n: number): number {
  return Number.isFinite(n) ? n : 0;
}

export function StackedAmountsABCD({
  aMain,
  bMain,
  cMain,
  dMain,
  className = '',
}: StackedAmountsABCDProps) {
  const amounts = [
    Math.abs(safeNum(aMain)),
    Math.abs(safeNum(bMain)),
    Math.abs(safeNum(cMain)),
    Math.abs(safeNum(dMain)),
  ];
  const total = amounts.reduce((s, x) => s + x, 0);

  if (total <= 0) {
    return (
      <div
        className={`rounded-lg border border-dashed border-slate-200 bg-slate-50/90 px-4 py-6 text-center text-sm text-slate-500 ${className}`}
        role="status"
      >
        No A+B+C+D totals to chart — amounts are zero or missing.
      </div>
    );
  }

  const ariaParts = SEGMENTS.map((seg, i) => {
    const share = amounts[i] / total;
    return `${seg.label}: ${formatCurrencyCompact(amounts[i])}, ${formatPct2(share)} of total`;
  });

  return (
    <div className={className}>
      <div
        className="flex h-11 w-full overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-300/80 print:h-8"
        role="img"
        aria-label={`Budget amounts by bucket (absolute values). ${ariaParts.join('. ')}.`}
      >
        {amounts.map((amt, i) => {
          const w = (amt / total) * 100;
          const share = amt / total;
          const seg = SEGMENTS[i];
          const title = `${seg.label}: ${formatCurrencyCompact(amt)} (${formatPct2(share)} of |A|+|B|+|C|+|D|)`;
          if (w <= 0) return null;
          return (
            <div
              key={seg.key}
              className="h-full min-w-0 shrink-0 transition-[width] duration-300 ease-out"
              style={{ width: `${w}%`, backgroundColor: seg.color }}
              title={title}
            />
          );
        })}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] font-semibold text-slate-700 print:text-[8px]">
        {SEGMENTS.map((seg, i) => {
          const share = total > 0 ? amounts[i] / total : 0;
          return (
            <li key={seg.key} className="inline-flex items-center gap-1.5">
              <span
                className="size-2.5 shrink-0 rounded-sm ring-1 ring-black/10"
                style={{ backgroundColor: seg.color }}
              />
              <span>{seg.label}</span>
              <span className="font-normal text-slate-500">
                {formatCurrencyCompact(amounts[i])} ({formatPct2(share)})
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
